import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Divider, Grid, TextField, Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useSnackbar } from "notistack";
import PageTitle from "../../common/PageTitle";
import Loader from "../../common/Loader";
import ProzoDataGrid, { useFirstPageOnChange } from "../../common/ProzoDataGrid";
import withLayout from "../../hocs/Layout";
import { apiConstants, getGeneric, patch, postGeneric } from "../../common/constants";
import { formatDate, parseDateAndTime } from "../../helpers/UtilityHelper";
import OrderView from "../EscalationManagement/OrderView";
import SectionCard from "./SectionCard";
import { ACCENTS, CARD_SX, StatusChip, rise } from "./emsUi";

import { gradientBtnSx } from "./ticketFormStyles";
import { NAVY, SUBTLE, PRIMARY_BLUE } from '../../styles/foundation/colors';
import { getStatusChipSx } from "../orders/list2/cellStyles";

/** A remark's files share the 20 MB limit a ticket's attachments have (EscalationDocService). */
const MAX_REMARK_FILES_BYTES = 20 * 1024 * 1024;
/** The file's own name from a signed S3 link (keys are "escalation/uploads/<uuid>-<name>"). */
const fileNameFromUrl = (url, i) => {
    try {
        const last = decodeURIComponent(new URL(url).pathname.split("/").pop() || "");
        return last.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i, "") || `Attachment ${i + 1}`;
    } catch (e) {
        return `Attachment ${i + 1}`;
    }
};

// Where a ticket is along Created → With Prozo → Resolved → Closed; waiting on the brand shows as
// its own step colour on "With Prozo".
const STEPS = [
    { key: "OPEN", label: "Created" },
    { key: "PENDING", label: "In progress" },
    { key: "RESOLVED", label: "Resolved" },
    { key: "CLOSED", label: "Closed" },
];
const TicketProgress = ({ escalation }) => {
    // A reopened ticket is back with Prozo: it shows on "In progress", not "Created".
    const stepKey = escalation.statusCategory === "OPEN" && escalation.reopenCount > 0 ? "PENDING" : escalation.statusCategory;
    const at = Math.max(0, STEPS.findIndex((st) => st.key === stepKey));
    const waiting = escalation.slaPausedAt && escalation.statusCategory === "PENDING";
    // A closed ticket has finished: the whole bar turns green.
    const closed = escalation.statusCategory === "CLOSED";
    return (
        <Box component="ol" aria-label="Ticket progress" sx={{ listStyle: "none", m: 0, px: { xs: 2, sm: 2.5 }, py: 1.75, display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1 }}>
            {STEPS.map((st, i) => {
                const done = i < at;
                const current = i === at;
                const a = closed ? ACCENTS.green : current && waiting ? ACCENTS.amber : done || current ? ACCENTS.blue : ACCENTS.grey;
                return (
                    <Box component="li" key={st.key} aria-current={current ? "step" : undefined} sx={{ minWidth: 0 }}>
                        <Box sx={{ height: 5, borderRadius: "999px", bgcolor: done || current ? a.solid : "#E6EAF0", opacity: current || closed ? 1 : done ? 0.55 : 1,
                            transition: "background-color 300ms" }} />
                        <Typography sx={{ fontSize: 12, mt: 0.75, fontWeight: current ? 800 : 600, color: current || closed ? a.solid : done ? NAVY : SUBTLE,
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {current && waiting ? "Waiting for you" : st.label}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
};

const Field = ({ label, value }) => (
    <Box>
        <Typography sx={{ fontSize: 12, color: SUBTLE, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</Typography>
        <Typography sx={{ fontSize: 13.5, color: NAVY, fontWeight: 600, mt: 0.25 }}>{value || "—"}</Typography>
    </Box>
);

const humanizeKey = (key) => key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());

// Same excludes list the existing (V1) ViewEms.js sends to order/search, so both pages get the
// same shaped response from the same generic endpoint.
const ORDER_SEARCH_EXCLUDES = "returnInfo,itemList.hsn,deliveryTime,itemList.itemUrl,itemList.itemDescription,actualDeliveryTat,proshipDeliveryTat,courierDeliveryTat,actualPickUpTime,lastStatusUpdateTime,edd,epd,lifeCycleComplete,labelUrl,courier,merchantDetail,courierId,version,order_history,orderManifest,invoiceUrl,courierPricingRTO,courierPricing,overriddenCourierAccountCode,isDiscrepancyRaised,discrepancyId,securityKey,sortCode,channelType,createdByUser,skipRule,modifiedByUser,rvp_reason,channel_name,customer_detail,cpErrorList,mongoLabelSignedUrlDetails,overriddenCourierRule,mongoInvoiceSignedUrlDetails,merchant,merchantPricing.merchantRateCard,merchantPricing.pricingObj,courierPricingRTO,modifiedByUser,lastModifiedDate,terminalStatus,merchantPricingRTO.merchantRateCard,merchantPricingRTO.pricingObj";

const relatedTicketColumns = (navigate) => [
    { field: 'escalationNumber', headerAlign: "left", headerName: 'Ticket #', flex: 0.4, align: 'left' },
    {
        field: 'awbNumber', headerAlign: "left", headerName: 'AWB', flex: 0.5, align: 'left',
        renderCell: ({ row }) => row?.awbNumber || "-",
    },
    { field: 'category', headerAlign: "left", headerName: 'Category', flex: 0.5, align: 'left' },
    { field: 'subCategory', headerAlign: "left", headerName: 'SubCategory', flex: 0.5, align: 'left' },
    { field: 'status', headerAlign: "left", headerName: 'Ticket Status', flex: 0.4, align: 'left' },
    {
        field: 'createdAt', headerAlign: "left", headerName: 'Created Date', flex: 0.35, align: 'left',
        renderCell: ({ row }) => row?.createdAt ? formatDate(row.createdAt) : '-',
    },
    {
        field: 'action', headerAlign: "left", headerName: 'Action', flex: 0.25, align: 'left', sortable: false,
        renderCell: ({ row }) => (
            <VisibilityIcon fontSize="small" sx={{ color: PRIMARY_BLUE, cursor: 'pointer' }}
                onClick={() => navigate(`/support-v2/view?ticketId=${row.id}`)} />
        ),
    },
];

/**
 * Ticket detail — merchant panel, Escalation Desk V2. Reached from index.js
 * (/support-v2/view?ticketId=...). Read-only status (no editor, no dropdown) — closing and
 * reopening are on the ticket list, the only status changes a merchant can make; a remark on a
 * ticket waiting for the brand hands it back to Prozo. Remarks are always added as EXTERNAL (the backend
 * forces this for a merchant-scoped caller regardless of what's sent — see
 * EscalationService.addRemark), so there's no INTERNAL/EXTERNAL visibility choice to show
 * either, unlike the admin panel's equivalent page. Reuses OrderView.js (from the existing V1
 * Help & Support module) for Shipment (AWB) Details — that component is plain order data, not
 * Freshdesk-specific, so it works as-is against the same order/search endpoint.
 */
const ViewTicket = () => {
    const [searchParams] = useSearchParams();
    const ticketId = searchParams.get("ticketId");
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [isLoading, setIsLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [remarkBody, setRemarkBody] = useState("");
    const [remarkSubmitting, setRemarkSubmitting] = useState(false);
    const [remarkFiles, setRemarkFiles] = useState([]);
    const remarkFileInput = useRef(null);
    const remarkFilesBytes = remarkFiles.reduce((sum, f) => sum + f.size, 0);
    const [reopenComment, setReopenComment] = useState("");
    const [reopening, setReopening] = useState(false);
    const [orderSearch, setOrderSearch] = useState(null);
    const [relatedTickets, setRelatedTickets] = useState([]);
    const [relatedPage, setRelatedPage] = useState(0);
    const [relatedSizePerPage, setRelatedSizePerPage] = useState(10);

    // Opening a related ticket keeps this page mounted, so start its related list on page 1.
    useFirstPageOnChange(ticketId, setRelatedPage);

    // Request id: only the latest load may paint, so a slow response for the previous ticket
    // (or an earlier reload) can't overwrite the one now open.
    const detailReqRef = useRef(0);
    const loadDetail = useCallback(async () => {
        if (!ticketId) return;
        const reqId = ++detailReqRef.current;
        setIsLoading(true);
        try {
            const response = await getGeneric(`${apiConstants.ESCALATION_DETAIL_V2}/${ticketId}`);
            if (reqId !== detailReqRef.current) return;
            setDetail(response?.data || null);
        } catch (error) {
            if (reqId !== detailReqRef.current) return;
            enqueueSnackbar("Could not load this ticket", { variant: "error" });
        } finally {
            if (reqId === detailReqRef.current) setIsLoading(false);
        }
    }, [ticketId, enqueueSnackbar]);

    useEffect(() => { loadDetail(); }, [loadDetail]);

    const escalation = detail?.escalation;
    const primaryAwb = escalation?.awbNumber || null;

    useEffect(() => {
        if (!primaryAwb) { setOrderSearch(null); return; }
        let cancelled = false;
        const fetchOrder = async () => {
            try {
                const response = await postGeneric(apiConstants.ORDER_LISTS, {
                    excludes: ORDER_SEARCH_EXCLUDES,
                    andfilter: [{ field: 'awbNumber', operator: 'eq', value: primaryAwb }],
                });
                if (!cancelled) setOrderSearch(response);
            } catch (error) {
                if (!cancelled) setOrderSearch(null);
            }
        };
        fetchOrder();
        return () => { cancelled = true; };
    }, [primaryAwb]);

    useEffect(() => {
        if (!primaryAwb) { setRelatedTickets([]); return; }
        let cancelled = false;
        const fetchRelated = async () => {
            try {
                const response = await postGeneric(apiConstants.ESCALATION_SEARCH_V2, { awbNumber: primaryAwb, size: 50 });
                if (cancelled) return;
                const others = (response?.data?.results || []).filter((t) => t.id !== ticketId);
                setRelatedTickets(others);
            } catch (error) {
                if (!cancelled) setRelatedTickets([]);
            }
        };
        fetchRelated();
        return () => { cancelled = true; };
    }, [primaryAwb, ticketId]);

    const customFieldEntries = useMemo(
        () => Object.entries(escalation?.customFieldValues || {}),
        [escalation]
    );

    // A resolved ticket takes no new comments: reopening it, with one, sends it back to Prozo.
    const reopenTicket = async () => {
        if (!reopenComment.trim()) return;
        setReopening(true);
        try {
            const statuses = await getGeneric(apiConstants.ESCALATION_STATUS_CONFIG_V2);
            const open = (statuses?.data || []).find((s) => s.category === "OPEN");
            if (!open) throw new Error("No open status is configured");
            await patch(`${apiConstants.BASE_LOCAL}${apiConstants.ESCALATION_DETAIL_V2}/${ticketId}/status`, { status: open.label, reason: reopenComment.trim() });
            enqueueSnackbar("Ticket reopened, it's back with Prozo's team", { variant: "success" });
            setReopenComment("");
            loadDetail();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || error?.message || "Could not reopen ticket", { variant: "error" });
        } finally {
            setReopening(false);
        }
    };

    const addRemarkFiles = (e) => {
        const picked = Array.from(e.target.files || []);
        e.target.value = "";
        setRemarkFiles((prev) => [...prev, ...picked.filter((f) => !prev.some((p) => p.name === f.name && p.size === f.size))]);
    };

    const submitRemark = async () => {
        if (!remarkBody.trim() && remarkFiles.length === 0) return;
        if (remarkFilesBytes > MAX_REMARK_FILES_BYTES) {
            enqueueSnackbar("Files can be 20 MB in total. Remove some and try again.", { variant: "error" });
            return;
        }
        setRemarkSubmitting(true);
        try {
            if (remarkFiles.length > 0) {
                const formData = new FormData();
                formData.append("data", JSON.stringify({ body: remarkBody }));
                remarkFiles.forEach((file) => formData.append("attachments", file));
                await postGeneric(`${apiConstants.ESCALATION_DETAIL_V2}/${ticketId}/remark`, formData);
            } else {
                await postGeneric(`${apiConstants.ESCALATION_DETAIL_V2}/${ticketId}/remark`, { body: remarkBody });
            }
            // A reply on a ticket waiting for the brand hands it back to Prozo (the server moves it).
            enqueueSnackbar(escalation?.slaPausedAt ? "Remark added — the ticket is back with Prozo's team" : "Remark added", { variant: "success" });
            setRemarkBody("");
            setRemarkFiles([]);
            loadDetail();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Could not add remark", { variant: "error" });
        } finally {
            setRemarkSubmitting(false);
        }
    };

    if (!ticketId) {
        return <Typography sx={{ p: 3 }}>No ticket selected.</Typography>;
    }

    const orderMatch = orderSearch?.data?.count === 1 && orderSearch?.data?.data?.[0]?.awb_number === primaryAwb;

    return (
        <>
            <PageTitle>Ticket {escalation?.escalationNumber || ""}</PageTitle>
            {isLoading && <Loader />}

            <Box sx={{ mb: 2 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/support-v2")} sx={{ textTransform: "none", fontWeight: 600, color: SUBTLE }}>
                    Back to tickets
                </Button>
            </Box>

            {escalation && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

                    {/* Header band: which ticket, where it stands, and how far along it is. */}
                    <Box sx={{ ...CARD_SX, ...rise(0), overflow: "hidden" }}>
                        <Box sx={{
                            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, px: { xs: 2, sm: 2.5 }, py: 2,
                            color: "#fff",
                            background: `radial-gradient(circle at 95% 0%, rgba(255,255,255,.18) 0, rgba(255,255,255,0) 36%), linear-gradient(118deg, #14263F 0%, #1E3A5F 50%, ${PRIMARY_BLUE} 100%)`,
                        }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                                <Box sx={{ width: 44, height: 44, borderRadius: "12px", flexShrink: 0, bgcolor: "rgba(255,255,255,.14)", border: "1px solid rgba(255,255,255,.22)",
                                    display: "grid", placeItems: "center" }}>
                                    <ForumOutlinedIcon sx={{ color: "#fff", fontSize: 22 }} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 19, fontWeight: 800 }}>Ticket {escalation.escalationNumber}</Typography>
                                    <Typography sx={{ fontSize: 12.5, opacity: 0.85, mt: 0.2 }}>
                                        {[escalation.category, escalation.subCategory].filter(Boolean).join(" › ")}
                                        {escalation.awbNumber ? ` · AWB ${escalation.awbNumber}` : ""}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ bgcolor: "#fff", borderRadius: "999px", p: 0.25 }}><StatusChip row={escalation} /></Box>
                        </Box>
                        <TicketProgress escalation={escalation} />
                    </Box>

                    <SectionCard icon={<AssignmentOutlinedIcon sx={{ color: PRIMARY_BLUE }} />} title="Ticket Details" sub="Overall summary of the ticket">
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}><Field label="Category" value={escalation.category} /></Grid>
                            <Grid item xs={12} md={4}><Field label="Sub Category" value={escalation.subCategory} /></Grid>
                            <Grid item xs={12} md={4}><Field label="Courier" value={escalation.courierName} /></Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="AWB" value={escalation.awbNumber} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="Created By" value={escalation.createdByName ? `${escalation.createdByName}${escalation.createdByEmail ? " | " + escalation.createdByEmail : ""}` : null} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {/* The AWB's current status, refreshed from the order every 5 minutes while the ticket is open. */}
                                <Typography sx={{ fontSize: 12, color: SUBTLE, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>Shipment Status</Typography>
                                {escalation.orderStatus
                                    ? <Chip size="small" label={escalation.orderStatus} sx={{ ...getStatusChipSx(escalation.orderStatus), fontSize: 12, mt: 0.4 }} />
                                    : <Typography sx={{ fontSize: 13.5, color: NAVY, fontWeight: 600, mt: 0.25 }}>—</Typography>}
                            </Grid>

                            <Grid item xs={12} md={4}><Field label="Created" value={escalation.createdAt ? formatDate(escalation.createdAt) : null} /></Grid>
                            <Grid item xs={12} md={4}><Field label="Updated" value={escalation.updatedAt ? formatDate(escalation.updatedAt) : null} /></Grid>
                            <Grid item xs={12} md={4} />
                            <Grid item xs={12} md={4}>
                                <Field label="First Response Due" value={escalation.firstResponseDueAt ? parseDateAndTime(escalation.firstResponseDueAt) : null} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="Resolution Due By" value={escalation.slaPausedAt ? "Paused — waiting for your reply"
                                    : escalation.resolutionDueAt ? parseDateAndTime(escalation.resolutionDueAt) : null} />
                            </Grid>
                            <Grid item xs={12} md={4} />

                            {escalation.description && (
                                <Grid item xs={12}><Field label="Description" value={escalation.description} /></Grid>
                            )}
                            {customFieldEntries.map(([key, value]) => (
                                <Grid item xs={12} md={4} key={key}><Field label={humanizeKey(key)} value={value} /></Grid>
                            ))}
                            {detail?.attachmentUrls?.length > 0 && (
                                <Grid item xs={12}>
                                    <Typography sx={{ fontSize: 12, color: SUBTLE, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, mb: 0.5 }}>
                                        Attachments
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                        {detail.attachmentUrls.map((url, i) => (
                                            <Button key={i} href={url} target="_blank" rel="noopener noreferrer" size="small"
                                                startIcon={<AttachFileOutlinedIcon fontSize="small" />} sx={{ textTransform: "none", fontSize: 12.5 }}>
                                                Attachment {i + 1}
                                            </Button>
                                        ))}
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </SectionCard>

                    {detail?.statusHistory?.length > 0 && (
                        <SectionCard icon={<HistoryOutlinedIcon sx={{ color: PRIMARY_BLUE }} />} title="Status History" sub="How this ticket has moved so far">
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                {detail.statusHistory.slice().reverse().map((h) => (
                                    <Box key={h.id} sx={{ display: "flex", alignItems: "baseline", gap: 1, fontSize: 12.5 }}>
                                        <Typography sx={{ fontSize: 12.5, color: SUBTLE, minWidth: 140 }}>
                                            {h.changedAt ? formatDate(h.changedAt) : ""}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12.5, color: NAVY }}>
                                            {h.fromStatus ? `${h.fromStatus} → ${h.toStatus}` : `Created as ${h.toStatus}`}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </SectionCard>
                    )}

                    <SectionCard icon={<ForumOutlinedIcon sx={{ color: PRIMARY_BLUE }} />} title="Remarks" sub="A record of updates and communication on this ticket">
                        {escalation.slaPausedAt && (
                            <Box sx={{ mb: 2, p: 1.5, borderRadius: "10px", bgcolor: "#FDF3E5", border: "1px solid #F3D9AE" }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#B45309" }}>Prozo is waiting for your reply</Typography>
                                <Typography sx={{ fontSize: 12.5, color: "#7A4A0A", mt: 0.25 }}>
                                    Add a remark below to send the ticket back to our team. If there&apos;s no reply within 72 hours (Sundays not counted), the ticket is closed.
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                            {(detail?.remarks || []).length === 0 && (
                                <Typography sx={{ fontSize: 12.5, color: SUBTLE }}>No remarks yet.</Typography>
                            )}
                            {(detail?.remarks || []).map((r) => (
                                <Box key={r.id} sx={{ border: "1px solid #EEF1F5", borderRadius: "10px", p: 1.5 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: NAVY }}>{r.authorName}</Typography>
                                        <Typography sx={{ fontSize: 12, color: SUBTLE, ml: "auto" }}>
                                            {r.createdAt ? formatDate(r.createdAt) : ""}
                                        </Typography>
                                    </Box>
                                    {r.body && <Typography sx={{ fontSize: 13, color: NAVY, whiteSpace: "pre-wrap" }}>{r.body}</Typography>}
                                    {r.attachmentUrls?.length > 0 && (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: r.body ? 1 : 0 }}>
                                            {r.attachmentUrls.map((url, j) => (
                                                <Button key={j} href={url} target="_blank" rel="noopener noreferrer" size="small" variant="outlined"
                                                    startIcon={<AttachFileOutlinedIcon fontSize="small" />}
                                                    sx={{ textTransform: "none", fontSize: 12.5, maxWidth: 260, "& .MuiButton-startIcon": { flexShrink: 0 } }}>
                                                    <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fileNameFromUrl(url, j)}</Box>
                                                </Button>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {escalation.statusCategory === "CLOSED" ? (
                            <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#F1F3F6", border: "1px solid #E3E7ED" }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>This ticket is closed</Typography>
                                <Typography sx={{ fontSize: 12.5, color: SUBTLE, mt: 0.25 }}>
                                    It can&apos;t take new comments or be reopened. Raise a new ticket if the issue comes back.
                                </Typography>
                            </Box>
                        ) : escalation.statusCategory === "RESOLVED" ? (
                            <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#E8F7F0", border: "1px solid #BFE6D3" }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F8A5F" }}>This ticket is resolved</Typography>
                                <Typography sx={{ fontSize: 12.5, color: SUBTLE, mt: 0.25, mb: 1.5 }}>
                                    If the issue isn&apos;t fixed, reopen it and tell Prozo&apos;s team what still needs doing. It closes on its own 72 hours after resolving.
                                </Typography>
                                <TextField multiline minRows={3} fullWidth placeholder="The parcel still hasn't been delivered" value={reopenComment}
                                    onChange={(e) => setReopenComment(e.target.value)} sx={{ mb: 1.5, "& .MuiInputBase-root": { bgcolor: "#fff" } }} />
                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                    <LoadingButton loading={reopening} variant="contained" disabled={!reopenComment.trim()} onClick={reopenTicket} sx={gradientBtnSx}>
                                        Reopen Ticket
                                    </LoadingButton>
                                </Box>
                            </Box>
                        ) : (<>
                        <TextField multiline minRows={3} fullWidth placeholder="Add a remark…" value={remarkBody}
                            onChange={(e) => setRemarkBody(e.target.value)} sx={{ mb: 1.5 }} />
                        {remarkFiles.length > 0 && (
                            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                                {remarkFiles.map((f, i) => (
                                    <Chip key={`${f.name}-${i}`} size="small" icon={<AttachFileOutlinedIcon sx={{ fontSize: 15 }} />} label={f.name}
                                        onDelete={() => setRemarkFiles((prev) => prev.filter((_, j) => j !== i))} sx={{ maxWidth: 260, fontWeight: 600, "& .MuiChip-label": { textTransform: "none" } }} />
                                ))}
                                <Typography sx={{ fontSize: 12, color: remarkFilesBytes > MAX_REMARK_FILES_BYTES ? ACCENTS.red.solid : SUBTLE }}>
                                    {(remarkFilesBytes / (1024 * 1024)).toFixed(1)} of 20 MB
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <input ref={remarkFileInput} type="file" multiple hidden onChange={addRemarkFiles} />
                            <Button size="small" variant="outlined" startIcon={<AttachFileOutlinedIcon />} onClick={() => remarkFileInput.current?.click()}
                                sx={{ textTransform: "none", fontWeight: 600 }}>
                                Attach files
                            </Button>
                            <Box sx={{ flex: 1 }} />
                            <LoadingButton loading={remarkSubmitting} variant="contained" disabled={!remarkBody.trim() && remarkFiles.length === 0}
                                onClick={submitRemark} sx={gradientBtnSx}>
                                Add Remark
                            </LoadingButton>
                        </Box>
                        </>)}
                    </SectionCard>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreOutlinedIcon />}>
                            <Box>
                                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Shipment (AWB) Details</Typography>
                                <Typography sx={{ fontSize: 12, color: SUBTLE }}>
                                    The data below provides details of the shipment this ticket was raised for.
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            {orderMatch
                                ? <OrderView resData={{ orderSearch }} />
                                : <Typography sx={{ textAlign: "center", fontSize: 13, color: SUBTLE, py: 2 }}>No data</Typography>}
                        </AccordionDetails>
                    </Accordion>

                    {relatedTickets.length > 0 && (
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreOutlinedIcon />}>
                                <Box>
                                    <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Other Related Tickets</Typography>
                                    <Typography sx={{ fontSize: 12, color: SUBTLE }}>Tickets with the same AWB.</Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <ProzoDataGrid
                                    columns={relatedTicketColumns(navigate)}
                                    rows={relatedTickets}
                                    pagination
                                    paginationMode="client"
                                    sizePerPage={relatedSizePerPage}
                                    setSizePerPage={setRelatedSizePerPage}
                                    page={relatedPage}
                                    setPage={setRelatedPage}
                                    hideFooterPagination={false}
                                    rowCount={relatedTickets.length}
                                    autoHeight
                                    getRowId={(row) => row.id}
                                />
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Box>
            )}
        </>
    );
};

export default withLayout(ViewTicket);
